import { Nullable } from 'global';
import { Path } from 'react-hook-form';
import { BasePrice } from 'modules/equipment/types';
import { ListingForm } from 'components/shared/TableListing';
import {
  ChargePrice,
  DiscountType,
  QuoteFeeResponse,
  QuoteCalcRowInput,
  QuoteCalcAttachType,
  QuoteCalcLineItemType,
  QuoteEquipmentResponse,
} from '@trnsact/trnsact-shared-types';

import { QuoteCalcRowPrice, QuoteCalcViewModel } from '../api/types';
import { QuoteChargeListingEntity, QuoteLineItemType } from '../types';

export interface QuoteCalcFieldUpdate {
  path: Path<ListingForm<QuoteChargeListingEntity>>;
  value: number;
}

export interface SavedQuoteRowsSource {
  equipment?: Nullable<QuoteEquipmentResponse[]>;
  fees?: Nullable<QuoteFeeResponse[]>;
}

class QuoteCalcMapper {
  public toPreviewRows(rows: QuoteChargeListingEntity[] = []): QuoteCalcRowInput[] {
    return rows.map(row => {
      if (row.type === QuoteLineItemType.Equipment && row.equipment) {
        return {
          type: QuoteLineItemType.Equipment,
          equipment: {
            equipmentId: row.equipment.equipmentId,
            price: this.toCalcPrice(row.equipment.price),
            attachments: (row.equipment.attachments ?? []).map(attachment => ({
              price: this.toCalcPrice(attachment.price),
            })),
          },
        };
      }

      const charge = row.charge;

      return {
        type: QuoteLineItemType.Invoice,
        charge: charge
          ? {
              type: charge.type,
              id: charge.id ?? null,
              label: charge.label ?? null,
              amount: charge.amount ?? null,
              parentId: charge.parentId ?? null,
              isTaxable: charge.isTaxable ?? null,
              amountType: charge.amountType ?? null,
              parentType: charge.parentType ?? null,
              isFromCommonRules: charge.isFromCommonRules ?? false,
              customerVisibility: charge.customerVisibility ?? null,
              price: this.toCalcPrice(charge.price),
              cap: charge.cap
                ? {
                    min: charge.cap.min ?? null,
                    max: charge.cap.max ?? null,
                  }
                : null,
              groupBucket: {
                id: charge.groupBucket.id,
                type: charge.groupBucket.type,
              },
            }
          : null,
      };
    });
  }

  public toInputSignature(rows: QuoteChargeListingEntity[] = []): string {
    const computedOutputs = new Set(['total', 'netPrice', 'retailPrice']);

    return JSON.stringify(this.toPreviewRows(rows), (key, value) => (computedOutputs.has(key) ? undefined : value));
  }

  public fromSavedQuoteRows(voId: string, quote: Nullable<SavedQuoteRowsSource>): QuoteCalcRowInput[] {
    const equipmentList = (quote?.equipment ?? []).filter((item): item is QuoteEquipmentResponse => Boolean(item));
    const feeList = (quote?.fees ?? []).filter((item): item is QuoteFeeResponse => Boolean(item));
    const equipmentIds = new Set(equipmentList.map(equipment => equipment.equipmentId));

    const equipmentRows: QuoteCalcRowInput[] = equipmentList.map(equipment => ({
      type: QuoteCalcLineItemType.Equipment,
      equipment: {
        equipmentId: equipment.equipmentId,
        price: this.toChargePriceInput(equipment.price),
        attachments: [],
      },
    }));

    const feeRows: QuoteCalcRowInput[] = feeList.map(fee => {
      const parentId = fee.parentId ?? null;
      const parentType = parentId ? (equipmentIds.has(parentId) ? 'equipment' : 'charge') : null;

      return {
        type: QuoteCalcLineItemType.Invoice,
        charge: {
          id: fee.id ?? null,
          parentId,
          parentType,
          type: fee.type,
          label: fee.description,
          amount: fee.amount ?? null,
          isTaxable: fee.isTaxable ?? null,
          amountType: fee.amountType ?? null,
          isFromCommonRules: fee.isFromCommonRules ?? false,
          customerVisibility: fee.customerVisibility ?? null,
          price: this.toChargePriceInput(fee.price),
          cap: fee.cap ? { min: fee.cap.min ?? null, max: fee.cap.max ?? null } : null,
          groupBucket: {
            id: fee.equipmentId ?? voId,
            type: fee.equipmentId ? QuoteCalcAttachType.Equipment : QuoteCalcAttachType.Invoice,
          },
        },
      };
    });

    return [...equipmentRows, ...feeRows];
  }

  private toChargePriceInput(price?: Nullable<ChargePrice>) {
    return {
      total: price?.total ?? 0,
      markup: price?.markup ?? 0,
      netPrice: price?.netPrice ?? 0,
      discount: price?.discount ?? 0,
      salesTax: price?.salesTax ?? 0,
      markupType: price?.markupType ?? DiscountType.Flat,
      discountType: price?.discountType ?? DiscountType.Flat,
      salesTaxType: price?.salesTaxType ?? DiscountType.Flat,
      retailPrice: price?.retailPrice ?? 0,
      wholesalePrice: price?.wholesalePrice ?? 0,
    };
  }

  public toFieldUpdates(
    rows: QuoteChargeListingEntity[] = [],
    calc: Nullable<QuoteCalcViewModel>
  ): QuoteCalcFieldUpdate[] {
    if (!calc?.rows?.length) return [];

    const updates: QuoteCalcFieldUpdate[] = [];

    rows.forEach((row, index) => {
      const calcRow = calc.rows[index];

      if (!calcRow) return;

      if (row.type === QuoteLineItemType.Equipment && row.equipment && calcRow.equipment) {
        this.pushPriceUpdates(updates, `bodyFields.${index}.equipment.price`, calcRow.equipment.price);

        (row.equipment.attachments ?? []).forEach((_, attachmentIndex) => {
          const calcAttachment = calcRow.equipment?.attachments?.[attachmentIndex];

          if (calcAttachment) {
            this.pushPriceUpdates(
              updates,
              `bodyFields.${index}.equipment.attachments.${attachmentIndex}.price`,
              calcAttachment.price
            );
          }
        });
      }

      if (row.type === QuoteLineItemType.Invoice && row.charge && calcRow.charge) {
        this.pushPriceUpdates(updates, `bodyFields.${index}.charge.price`, calcRow.charge.price);
      }
    });

    return updates;
  }

  private pushPriceUpdates(updates: QuoteCalcFieldUpdate[], basePath: string, calcPrice: QuoteCalcRowPrice): void {
    if (calcPrice.total != null)
      updates.push({
        path: `${basePath}.total` as Path<ListingForm<QuoteChargeListingEntity>>,
        value: calcPrice.total,
      });

    if (calcPrice.netPrice != null)
      updates.push({
        path: `${basePath}.netPrice` as Path<ListingForm<QuoteChargeListingEntity>>,
        value: calcPrice.netPrice,
      });

    if (calcPrice.retailPrice != null)
      updates.push({
        path: `${basePath}.retailPrice` as Path<ListingForm<QuoteChargeListingEntity>>,
        value: calcPrice.retailPrice,
      });
  }

  private toCalcPrice = (price: BasePrice) => ({
    total: price.total ?? 0,
    markup: price.markup ?? 0,
    netPrice: price.netPrice ?? 0,
    discount: price.discount ?? 0,
    salesTax: price.salesTax ?? 0,
    markupType: price.markupType,
    discountType: price.discountType,
    salesTaxType: price.salesTaxType,
    retailPrice: price.retailPrice ?? 0,
    wholesalePrice: price.wholesalePrice ?? 0,
  });
}

export const quoteCalcMapper = new QuoteCalcMapper();
